// src/components/UI.jsx
import { useEffect, useId } from "react";
import { C, F, TYPE, tx, card, getRank, getNext, pct } from "../constants";
import { iconFor } from "./icons";
import { PartnerStrip } from "./PartnerLockup";

// ── Material Symbol helper ────────────────────────────────────────────────────
export function Icon({ name, size = 24, fill = false, color, style = {}, label, strokeWidth }) {
  const Glyph = iconFor(name);
  // Icons are hidden from the accessibility tree by default — they nearly always
  // sit beside text that already says the same thing. Pass `label` when the icon
  // is the only thing carrying the meaning.
  //
  // `fill` used to switch the Material variable font's FILL axis. Lucide is a
  // stroke set, so the filled look is expressed the way a stroke set does it:
  // a heavier stroke. Call sites that pass `fill` keep working and still read
  // as emphasised.
  return (
    <Glyph
      size={size}
      color={color || "currentColor"}
      strokeWidth={strokeWidth ?? (fill ? 2.5 : 2)}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      style={{ flexShrink: 0, verticalAlign: "middle", ...style }}
    />
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 24, color = C.primary, label = "Loading" }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{ width: size, height: size, borderRadius: "50%", border: `2.5px solid ${C.border}`, borderTop: `2.5px solid ${color}`, animation: "spin .8s linear infinite", flexShrink: 0 }}
    />
  );
}

// ── Toast notification ────────────────────────────────────────────────────────
export function Toast({ msg, color = C.primary, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div role="status" aria-live="polite" style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: C.card, border: `1px solid ${color}40`, color: C.text, padding: "12px 20px", borderRadius: 50, fontSize: 13, fontWeight: 600, fontFamily: F, boxShadow: C.shadowLg, whiteSpace: "nowrap", animation: "bounceIn .4s ease", maxWidth: "88vw", textAlign: "center", display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 7, height: 7, borderRadius: "50%", background: color, flexShrink: 0 }} />
      {msg}
    </div>
  );
}

// ── FloatPts ──────────────────────────────────────────────────────────────────
export function FloatPts({ pts, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1200); return () => clearTimeout(t); }, [onDone]);
  return (
    <div style={{ position: "fixed", top: "35%", left: "50%", transform: "translateX(-50%)", zIndex: 9999, pointerEvents: "none", whiteSpace: "nowrap", animation: "slideUp .4s ease, fadeIn .4s ease", fontFamily: F, fontSize: 36, fontWeight: 800, color: C.primary, textShadow: "0 2px 10px rgba(16,36,46,.18)" }}>
      +{pts} pts
    </div>
  );
}

// ── XP Progress Bar ───────────────────────────────────────────────────────────
export function XPBar({ pts }) {
  const rank = getRank(pts), nr = getNext(pts);
  const p = nr ? pct(pts - rank.min, rank.max - rank.min) : 100;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <Icon name={rank.icon} size={14} color={rank.color} />
          <span style={{ fontSize: 11, color: rank.color, fontWeight: 600 }}>{rank.title}</span>
        </div>
        {nr && <span style={{ fontSize: 11, color: C.muted }}>{(rank.max - pts).toLocaleString()} to {nr.title}</span>}
      </div>
      <div style={{ height: 8, background: C.faint, borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${p}%`, background: rank.color, borderRadius: 99, transition: "width 1s ease" }} />
      </div>
    </div>
  );
}

// ── Top App Bar ───────────────────────────────────────────────────────────────
export function TopBar({ profile, unreadCount = 0, onBellClick, roleColor = C.primary }) {
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 100, background: `${C.bg}ee`, backdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}`, padding: "10px 16px 10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {/* Avatar + name */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: `${roleColor}20`, border: `2px solid ${roleColor}40`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="person" size={18} color={roleColor} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: F }}>
              {profile?.name?.split(" ")[0] || "CoastClear"}
            </div>
            <div style={{ fontSize: 10, color: C.muted }}>
              {profile?.role === "cleanup" ? "Cleanup Crew" : profile?.role === "ngo" ? "NGO" : "Reporter"}
            </div>
          </div>
        </div>
        {/* Right side: streak + pts + bell */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(255,107,107,0.12)", border: "1px solid rgba(255,107,107,0.2)", borderRadius: 99, padding: "5px 10px" }}>
            <Icon name="local_fire_department" size={14} color={C.coral} />
            <span style={{ fontSize: 12, fontWeight: 700, color: C.coral, fontFamily: F }}>{profile?.streak || 0}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: `${C.primary}12`, border: `1px solid ${C.primary}25`, borderRadius: 99, padding: "5px 10px" }}>
            <Icon name="stars" size={14} color={C.primary} />
            <span style={{ fontSize: 12, fontWeight: 700, color: C.primary, fontFamily: F }}>{(profile?.pts || 0).toLocaleString()}</span>
          </div>
          <button
            onClick={onBellClick}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
            style={{ position: "relative", background: C.faint, border: `1px solid ${C.border}`, borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          >
            <Icon name="notifications" size={18} color={C.text} />
            {unreadCount > 0 && (
              <div style={{ position: "absolute", top: -3, right: -3, width: 16, height: 16, borderRadius: "50%", background: C.coral, fontSize: 9, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", border: `1.5px solid ${C.bg}` }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Language switch ───────────────────────────────────────────────────────────
// CoastClear is trilingual, and which of the three a volunteer reads is not
// something a device locale can be trusted to know: a shared phone, a hand-me-
// down handset or a default-English ROM all give the wrong answer, and Pidgin
// is not a locale a phone offers at all. So the choice is always explicit and
// always reachable — never inferred and never buried.
//
// Rendered here rather than per screen so the wording, order and hit targets
// are the same everywhere it appears.
const LANGUAGES = [
  { id: "en", label: "EN", full: "English" },
  { id: "fr", label: "FR", full: "Français" },
  { id: "pid", label: "Pid", full: "Pidgin" },
];

export function LangSwitch({ lang, setLang, size = "sm" }) {
  const pad = size === "sm" ? "5px 9px" : "8px 14px";
  const fs = size === "sm" ? 11.5 : 13;
  return (
    <div role="group" aria-label="Language" style={{ display: "flex", gap: 4 }}>
      {LANGUAGES.map((l) => {
        const active = lang === l.id;
        return (
          <button
            key={l.id}
            onClick={() => setLang(l.id)}
            aria-pressed={active}
            title={l.full}
            style={{
              padding: pad,
              borderRadius: 8,
              border: `1px solid ${active ? C.pBorder : C.border}`,
              background: active ? C.pLight : "transparent",
              color: active ? C.primary : C.muted,
              fontSize: fs,
              fontWeight: active ? 800 : 600,
              cursor: "pointer",
              fontFamily: F,
              minHeight: 32,
            }}
          >
            {size === "sm" ? l.label : l.full}
          </button>
        );
      })}
    </div>
  );
}

// ── Navigation ────────────────────────────────────────────────────────────────
// One list of destinations, rendered two ways. On a phone it is a thumb-reachable
// bar across the bottom; on a desktop it is a side rail, because a bottom bar on
// a 1280px screen puts the navigation as far from the content as it can get.
const navItems = (role, lang) => {
  const l = lang || "en";
  return [
    { id: "home",    icon: "home",          label: tx(l, "home") },
    { id: "report",  icon: role === "ngo" ? "monitoring" : "add_circle", label: tx(l, role === "ngo" ? "map" : "report") },
    // Environmental-health module — the surveillance/risk/response side of the app.
    { id: "eh",      icon: "ecg_heart",     label: { en: "Health", fr: "Santé", pid: "Health" }[l] || "Health" },
    { id: "map",     icon: "map",           label: tx(l, "map") },
    { id: "learn",   icon: "menu_book",     label: tx(l, "learn") },
    { id: "profile", icon: "person",        label: tx(l, "profile") },
  ];
};

const navColor = (role) => (role === "cleanup" ? C.green : role === "ngo" ? C.gold : C.primary);

/** Phone and tablet: fixed to the bottom, within the content column. */
export function BottomNav({ tab, setTab, role, lang, maxWidth = 480 }) {
  const roleColor = navColor(role);
  return (
    <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth, background: `${C.bg}f2`, backdropFilter: "blur(20px)", borderTop: `1px solid ${C.border}`, padding: "8px 2px 20px", zIndex: 200 }}>
      <div role="tablist" aria-label="Main sections" style={{ display: "flex", justifyContent: "space-around" }}>
        {navItems(role, lang).map(n => {
          const active = tab === n.id;
          return (
            <button key={n.id} onClick={() => setTab(n.id)} role="tab" aria-selected={active} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: "6px 6px", borderRadius: 12, background: active ? `${roleColor}18` : "transparent", border: "none", cursor: "pointer", minWidth: 44, transition: "all .2s" }}>
              <Icon name={n.icon} size={21} color={active ? roleColor : C.muted} fill={active} />
              <span style={{ fontSize: 9.5, fontWeight: active ? 700 : 500, color: active ? roleColor : C.muted, fontFamily: F }}>{n.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Desktop: a side rail. Same destinations, same order, labels always visible. */
export function SideNav({ tab, setTab, role, lang, width = 216 }) {
  const roleColor = navColor(role);
  return (
    <nav
      aria-label="Main sections"
      style={{
        width,
        flexShrink: 0,
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
        height: "100vh",
        borderRight: `1px solid ${C.border}`,
        background: C.card,
        padding: "18px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 10px 16px" }}>
        <Icon name="ecg_heart" size={21} color={C.primary} />
        <span style={{ fontSize: 16, fontWeight: 800, color: C.text, letterSpacing: "-0.3px", fontFamily: F }}>
          Coast<span style={{ color: C.primary }}>Clear</span>
        </span>
      </div>
      {/* The two partner marks, stacked under the CoastClear wordmark and
          above the navigation, so the association is made once at the top of
          the rail and then stays out of the way. The rail is the only place
          on a desktop that shows them — the mobile shell uses a bar under the
          top bar instead (see App.js). */}
      {/* No plates here: the rail is already C.card, the same white the marks
          are supplied on, so they sit on it seamlessly. That buys the width to
          run them at 40 — ~148px of the rail's ~172px usable — which is the
          largest either logo appears anywhere in the app proper. */}
      <div style={{ padding: "0 10px 16px", marginBottom: 4, borderBottom: `1px solid ${C.border}` }}>
        <PartnerStrip lang={lang} base={40} align="flex-start" style={{ gap: 9 }} />
      </div>

      <div role="tablist" aria-label="Main sections" style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 12 }}>
        {navItems(role, lang).map(n => {
          const active = tab === n.id;
          return (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              role="tab"
              aria-selected={active}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                padding: "10px 12px",
                borderRadius: 10,
                background: active ? `${roleColor}14` : "transparent",
                border: "none",
                borderLeft: `3px solid ${active ? roleColor : "transparent"}`,
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                transition: "background .15s",
              }}
            >
              <Icon name={n.icon} size={19} color={active ? roleColor : C.muted} fill={active} />
              <span style={{ fontSize: 13.5, fontWeight: active ? 700 : 500, color: active ? roleColor : C.muted, fontFamily: F }}>{n.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ── Input field ───────────────────────────────────────────────────────────────
export function Input({ label, value, onChange, type = "text", placeholder, error, icon, rightEl, autoComplete, id }) {
  // A real <label for> rather than a styled div, so tapping the label focuses
  // the field and a screen reader announces the two together.
  const fieldId = useId();
  const inputId = id || fieldId;
  const errorId = `${inputId}-error`;
  return (
    <div style={{ marginBottom: error ? 4 : 0 }}>
      {label && (
        <label htmlFor={inputId} style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, fontFamily: F }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        {icon && <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><Icon name={icon} size={18} color={C.dim} /></div>}
        <input
          id={inputId}
          type={type} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          style={{ width: "100%", padding: `14px ${rightEl ? 44 : 16}px 14px ${icon ? 44 : 16}px`, background: C.faint, border: `1.5px solid ${error ? C.coral : C.border}`, borderRadius: 12, color: C.text, fontSize: 16, fontFamily: F, transition: "border-color .2s" }}
          onFocus={e => e.target.style.borderColor = C.primary}
          onBlur={e => e.target.style.borderColor = error ? C.coral : C.border}
        />
        {rightEl && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>{rightEl}</div>}
      </div>
      {error && (
        <div id={errorId} role="alert" style={{ fontSize: 11.5, color: C.coral, marginTop: 5, display: "flex", gap: 5, alignItems: "flex-start", lineHeight: 1.4 }}>
          <Icon name="error" size={13} color={C.coral} style={{ flexShrink: 0, marginTop: 1 }} />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Primary button ────────────────────────────────────────────────────────────
export function PrimaryBtn({ children, onClick, disabled, loading: isLoading, color = C.primary, fullWidth = true, style = {} }) {
  return (
    <button
      onClick={onClick} disabled={disabled || isLoading}
      style={{ width: fullWidth ? "100%" : "auto", padding: "15px 24px", background: disabled || isLoading ? C.faint : color, border: `1px solid ${disabled || isLoading ? C.border : color}`, borderRadius: 99, fontFamily: F, fontSize: 15, fontWeight: 700, color: disabled || isLoading ? C.dim : C.bg, cursor: disabled || isLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background .2s, opacity .2s", ...style }}
    >
      {isLoading ? <><Spinner size={18} color={C.bg} /> Loading...</> : children}
    </button>
  );
}

// ── Edge states ──────────────────────────────────────────────────────────────
// The three states every data view in this app owes the reader, defined once so
// they look the same everywhere. A volunteer on a bad link hits all three in a
// single field day, so none of them may be a bare spinner or a shrug.

/** Content-shaped placeholder, sized like the rows it stands in for. */
export function Skeleton({ rows = 3, height = 64, gap = 8 }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: "flex", flexDirection: "column", gap }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            height,
            borderRadius: 12,
            background: `linear-gradient(90deg, ${C.faint} 25%, rgba(16,36,46,0.10) 37%, ${C.faint} 63%)`,
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s ease infinite",
          }}
        />
      ))}
    </div>
  );
}

/**
 * Nothing here yet — and what to do about it. The action should be the thing
 * that fills the view, never a dead-end "check back later".
 */
export function Empty({ icon = "inbox", title, sub, actionLabel, onAction }) {
  return (
    <div style={{ ...card("flat"), padding: "34px 20px", textAlign: "center", fontFamily: F }}>
      <Icon name={icon} size={34} color={C.dim} />
      <div style={{ ...TYPE.section, color: C.text, margin: "10px 0 5px" }}>{title}</div>
      {sub && <div style={{ ...TYPE.body, color: C.muted, maxWidth: 300, margin: "0 auto" }}>{sub}</div>}
      {actionLabel && onAction && (
        <button
          onClick={onAction}
          style={{ marginTop: 14, background: `${C.primary}18`, border: `1px solid ${C.primary}44`, borderRadius: 99, padding: "9px 18px", fontSize: 12.5, fontWeight: 700, color: C.primary, cursor: "pointer", fontFamily: F }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Something broke. Names what failed in plain words, keeps the technical detail
 * available but out of the way, and always offers the retry.
 */
export function ErrorState({ title, detail, onRetry, retryLabel = "Try again" }) {
  return (
    <div role="alert" style={{ ...card("keyed", C.coral), textAlign: "center", padding: "26px 20px", fontFamily: F }}>
      <Icon name="cloud_off" size={30} color={C.coral} />
      <div style={{ ...TYPE.section, color: C.text, margin: "10px 0 5px" }}>{title}</div>
      {detail && <div style={{ ...TYPE.body, color: C.muted, maxWidth: 320, margin: "0 auto" }}>{detail}</div>}
      {onRetry && (
        <button
          onClick={onRetry}
          style={{ marginTop: 14, background: `${C.coral}1e`, border: `1px solid ${C.coral}55`, borderRadius: 99, padding: "9px 18px", fontSize: 12.5, fontWeight: 700, color: C.coral, cursor: "pointer", fontFamily: F, display: "inline-flex", alignItems: "center", gap: 7 }}
        >
          <Icon name="refresh" size={15} color={C.coral} />
          {retryLabel}
        </button>
      )}
    </div>
  );
}

/**
 * Persistent connection state. Volunteers work where the signal drops, so this
 * is not a transient toast — it stays on screen for as long as it is true, and
 * says whether anything is waiting to go up rather than just "offline".
 */
export function OfflineBar({ online, queued = 0, onRetry }) {
  if (online && !queued) return null;
  const tone = online ? C.primary : C.gold;
  const text = !online
    ? queued
      ? `Offline — ${queued} record${queued === 1 ? "" : "s"} saved on this phone, waiting for signal`
      : "Offline — everything you capture is saved on this phone"
    : `Back online — ${queued} record${queued === 1 ? "" : "s"} still waiting to upload`;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{ display: "flex", alignItems: "center", gap: 9, background: `${tone}14`, borderBottom: `1px solid ${tone}38`, padding: "8px 16px", fontFamily: F }}
    >
      <Icon name={online ? "cloud_upload" : "cloud_off"} size={15} color={tone} />
      <span style={{ flex: 1, ...TYPE.meta, color: C.muted }}>{text}</span>
      {online && queued > 0 && onRetry && (
        <button
          onClick={onRetry}
          style={{ background: `${tone}22`, border: `1px solid ${tone}44`, borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: tone, cursor: "pointer", fontFamily: F, flexShrink: 0 }}
        >
          Upload now
        </button>
      )}
    </div>
  );
}
